/**
 * Learning Bank.
 *
 * Dos ejes ORTOGONALES (cambio aprobado #2):
 *   kind → qué FORMA tiene el elemento.   Obligatorio.
 *   area → qué ENTRENA.                   Nullable (Ajuste 1).
 *
 * La spec original mezclaba los dos en un solo campo `type`, con el resultado
 * de que `lexical retrieval` —el ejemplo que la propia spec usa para ilustrar
 * el Día 5— no se podía contar.
 *
 * AJUSTE 1: `area` es opcional cuando no hay clasificación natural. La interfaz
 * puede SUGERIR; nunca debe FORZAR. Solo los elementos CON área participan en
 * la puntuación del Día 5. Nunca inventar una categoría para poder puntuar.
 */

import type { AreaOpcional } from './areas.js';
import type { Id } from './ids.js';
import type { Prioridad } from './prioridad.js';
import type { MetadatosSync } from './sincronizacion.js';

/** Qué forma tiene el elemento. OBLIGATORIO. */
export const KINDS = ['error', 'expresion', 'situacion_trabajo', 'target'] as const;
export type Kind = (typeof KINDS)[number];

export const NOMBRE_KIND: Record<Kind, string> = {
  error: 'Error',
  expresion: 'Expresión útil',
  situacion_trabajo: 'Situación de trabajo',
  target: 'Target de pronunciación',
};

/** De dónde salió. */
export const FUENTES = [
  'chatgpt',
  'trabajo',
  'conversacion',
  'listening',
  'baseline',
  'manual',
] as const;
export type FuenteElemento = (typeof FUENTES)[number];

/**
 * Estado del elemento.
 *
 * Los `dominado` salen de la vista activa y del Día 4, pero NO SE BORRAN NUNCA:
 * quedan como historial (CA-5.2).
 */
export const ESTADOS_ELEMENTO = ['activo', 'aprendiendo', 'dominado', 'archivado'] as const;
export type EstadoElemento = (typeof ESTADOS_ELEMENTO)[number];

export interface ElementoBanco extends MetadatosSync {
  id: Id;
  kind: Kind;
  /** NULLABLE a propósito. `null` = sin clasificar, opción legítima. */
  area: AreaOpcional;
  /** El contexto o la situación. Es lo que se muestra ANTES del intento. */
  contextoOriginal: string;
  /** La corrección o el target. Queda OCULTO hasta que el usuario lo pide. */
  correccionTarget: string;
  notas?: string;
  fuente: FuenteElemento;
  /** Manual siempre. Ninguna regla la calcula (Decisión D). */
  prioridad: Prioridad;
  estado: EstadoElemento;
  /** Sube SOLO con la acción explícita «volvió a pasar» (CA-5.8). */
  vecesOcurrido: number;
  /**
   * Clave estable del origen, cuando el elemento nació de una siembra.
   *
   * Es lo que hace idempotente el cierre del baseline: volver a guardarlo
   * actualiza el mismo elemento en vez de crear un duplicado, y una edición
   * posterior conserva su identidad.
   */
  origenClave?: string;
  creadoEn: string;
  ultimaVezVistoEn?: string;
  ultimaPracticaEn?: string;
  etiquetas: string[];
}

/** Los que alimentan el Día 4 y la puntuación del Día 5. */
export function estaActivo(elemento: ElementoBanco): boolean {
  return (
    !elemento.deleted &&
    (elemento.estado === 'activo' || elemento.estado === 'aprendiendo')
  );
}

/**
 * Los que participan en la puntuación del Día 5.
 *
 * AJUSTE 1: solo los que tienen área. Un banco entero sin clasificar produce
 * «datos insuficientes», no una recomendación falsa.
 */
export function participaEnPuntuacion(
  elemento: ElementoBanco,
): elemento is ElementoBanco & { area: NonNullable<AreaOpcional> } {
  return estaActivo(elemento) && elemento.area !== null;
}
